import { useEffect, useId, useRef } from 'react'
import { useQuery } from '@tanstack/react-query'
import { createPortal } from 'react-dom'
import { analyticsApi, getErrorMessage } from '@/lib/api'
import { motion, AnimatePresence, useReducedMotion } from 'framer-motion'
import { X, ChartLine, ChatCircle, UsersThree, WarningCircle, ArrowClockwise } from '@phosphor-icons/react'
import {
  LineChart, Line, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
} from 'recharts'
import type {
  CampaignDetailData, CampaignDetailKpiKey, InboxHealth, KpiMetric, PipelineStage,
} from '@/types/analytics'
import { sentimentDonutData } from '@/lib/sentiment'
import KpiCard from './KpiCard'
import { Select, type SelectOption } from '@/components/ui/Select'
import { Envelope } from '@phosphor-icons/react'
import { BRAND_SERIES_COLOR } from './chartTheme'
import { sequentialStep } from './chartTheme'
import { Skeleton } from '@/components/ui/Skeleton'
import { campaignStatusMeta } from '@/lib/campaignStatus'
import { useScrollLock } from '@/lib/useScrollLock'
import { cn } from '@/lib/utils'
import { seriesColor, seriesDash, tooltipContentStyle, useChartTheme } from './chartTheme'

interface CampaignDrillDownProps {
  campaignId: number | null
  onClose: () => void
  userId?: number | null
  /** The window selected in the page header, carried in so the drawer reports
   *  the same period the list behind it does. Without these the request fell
   *  back to the API's own "30d" default, so a page set to 7d or 90d opened a
   *  drawer quietly answering about 30 days instead. `period` is null when a
   *  custom date range is in use, in which case the two dates carry it. */
  period?: string | null
  startDate?: string | null
  endDate?: string | null
  /** One mailbox address, or undefined for the combined view. Carried in from
   *  the page header so the drawer answers the same question the list behind
   *  it is answering. */
  senderEmail?: string
  /** The page header's mailbox list and its current value, so the filter can
   *  be changed from inside the drawer. The drawer covers the page header, so
   *  without this a user has to close it to re-scope what they are reading.
   *  Changing it here changes the page's own filter — the list behind the
   *  drawer keeps answering the same question the drawer does. */
  mailboxOptions?: SelectOption[]
  mailboxValue?: string
  onMailboxChange?: (value: string) => void
}

/** Everything inside the drawer a keyboard user can land on (§16 focus trap). */
const FOCUSABLE_SELECTOR =
  'button:not([disabled]), [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'

/**
 * Does this funnel have anything in it?
 *
 * Every stage is always returned, so `stages.length` is 4 whether the campaign
 * reached four thousand people or nobody — testing the array's length would
 * draw four empty bars and call it data. The emptiness is in the counts.
 */
export function pipelineIsEmpty(stages: PipelineStage[]): boolean {
  return stages.every((stage) => stage.count <= 0)
}

/**
 * How many contacts a campaign reached, which is the funnel's denominator.
 *
 * Not the sum of the four stages: a contact can sit in an off-ladder state
 * (unqualified) or carry no status at all, and those people were still
 * emailed. Stated here so the caption under the funnel says what the
 * percentages are a share OF.
 */
export function pipelineReached(stages: PipelineStage[]): number {
  const top = stages[0]
  if (!top || top.pct <= 0) {
    // With no share to divide by, the honest answer is what we can see.
    return stages.reduce((sum, stage) => sum + stage.count, 0)
  }
  return Math.round((top.count / top.pct) * 100)
}

/** Bar width is the share itself, clamped so bad data cannot overflow the track. */
export function funnelWidth(pct: number): number {
  if (!Number.isFinite(pct)) return 0
  return Math.min(Math.max(pct, 0), 100)
}

/**
 * Timeline series in lifecycle order. Hue and dash are read from the canonical
 * scale (§61) rather than declared here — this file used to own private
 * `CHART_*` constants that painted `opened` emerald and `replied` violet, the
 * exact inverse of the Overview and Campaigns tabs, so a reader who learned
 * "green = replies" on one tab read green as "opened" one click later.
 *
 * `name` is what the legend and tooltip print: the raw `dataKey` would surface
 * the backend field name to the user.
 */
export const TIMELINE_SERIES = [
  { key: 'sent',    label: 'Sent'    },
  { key: 'opened',  label: 'Opened'  },
  { key: 'clicked', label: 'Clicked' },
  { key: 'replied', label: 'Replied' },
] as const

/**
 * The API types this as `number`, but a campaign that has sent nothing yet comes
 * back with `null` counters — an unguarded read there threw and took the whole
 * drawer down with it. (The percentage formatter that stood next to this is
 * gone: every rate on the view now goes through KpiCard, which does its own
 * null handling.)
 */
const count = (v: number | null | undefined) => (v ?? 0).toLocaleString()
const pct = (v: number | null | undefined) => `${(v ?? 0).toFixed(1)}%`

/**
 * The six cards, in the order the Overview tab shows them. `format` and
 * `invertColor` are the only per-card differences: a rising bounce rate is bad
 * news, so its badge has to be red when every other card's rise is green.
 */
export const KPI_CARDS: {
  key: CampaignDetailKpiKey
  label: string
  format: 'number' | 'percent'
  invert?: boolean
}[] = [
  { key: 'emails_sent',     label: 'Emails sent',     format: 'number'  },
  { key: 'open_rate',       label: 'Open rate',       format: 'percent' },
  { key: 'click_rate',      label: 'Click rate',      format: 'percent' },
  { key: 'reply_rate',      label: 'Reply rate',      format: 'percent' },
  { key: 'bounce_rate',     label: 'Bounce rate',     format: 'percent', invert: true },
  { key: 'engagement_rate', label: 'Engagement rate', format: 'percent' },
]

/**
 * A card must render even if the KPI block is missing — a response cached from
 * before this block existed, or a campaign with nothing to report. The drawer
 * has been taken down once already by an unguarded read of a rate that came
 * back null, so every access here is defended rather than asserted.
 */
const metricOr0 = (kpis: CampaignDetailData['kpis'] | undefined, key: CampaignDetailKpiKey): KpiMetric =>
  kpis?.[key] ?? { value: 0, previous: null }

/** Period token to the wording the page's own picker uses, so the drawer names
 *  the window the same way the control that set it does. */
const PERIOD_LABELS: Record<string, string> = {
  '7d': 'Last 7 days',
  '30d': 'Last 30 days',
  '90d': 'Last 90 days',
  all: 'All time',
}

/**
 * The timeline's buckets arrive as the backend's own keys: "YYYY-MM-DD" for a
 * daily window, "IYYY-Www" for a weekly one (90 days and all time). Neither
 * is a label a reader should have to decode on an axis, so these turn them
 * into "30 Sep" / "Week 40", and the long form the tooltip uses.
 */
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

export function isWeeklyBucket(bucket: string): boolean {
  return /^\d{4}-W\d{2}$/.test(bucket)
}

export function formatBucket(bucket: string, long = false): string {
  const week = /^(\d{4})-W(\d{2})$/.exec(bucket)
  if (week) {
    const n = parseInt(week[2], 10)
    return long ? `Week ${n}, ${week[1]}` : `Week ${n}`
  }
  const day = /^(\d{4})-(\d{2})-(\d{2})$/.exec(bucket)
  if (day) {
    const d = parseInt(day[3], 10)
    const m = MONTHS[parseInt(day[2], 10) - 1] ?? day[2]
    return long ? `${d} ${m} ${day[1]}` : `${d} ${m}`
  }
  return bucket
}

/** "by day" or "by week", read off the data rather than the period, so the
 *  heading can never contradict the buckets actually plotted. */
export function timelineHeading(timeline: { date: string }[]): string {
  const weekly = timeline.length > 0 && isWeeklyBucket(timeline[0].date)
  return weekly ? 'Sends and engagement by week' : 'Sends and engagement by day'
}

/**
 * Bars up to ten buckets, lines beyond. Almost every campaign sends in one or
 * two bursts, so its timeline is one or two buckets, and a line chart with no
 * dots drew NOTHING for them: a single point has no neighbour to connect to.
 * The chart looked empty over a campaign that had sent dozens of emails. Bars
 * are readable at any count down to one.
 */
export const TIMELINE_BAR_MAX = 10
export function timelineChartKind(bucketCount: number): 'bar' | 'line' {
  return bucketCount <= TIMELINE_BAR_MAX ? 'bar' : 'line'
}

/** Whether any opens, clicks or replies exist at all in the plotted window --
 *  when none do, three of the four series sit flat on the baseline and the
 *  chart needs to say so in words. */
export function hasEngagement(timeline: { opened: number; clicked: number; replied: number }[]): boolean {
  return timeline.some((b) => (b.opened ?? 0) + (b.clicked ?? 0) + (b.replied ?? 0) > 0)
}

/**
 * One line saying what the metric cards below actually cover.
 *
 * Needed because the cards and the campaign's own header answer different
 * questions: the header (and the row in the list behind the drawer) carries
 * lifetime counters, while the cards are scoped to the selected window and to
 * one inbox. Without this line a reader sees "60%" in the list and "20%" on the
 * card and has no way to know which is wrong — neither is.
 */
export function describeScope(
  period: string | null | undefined,
  startDate: string | null | undefined,
  endDate: string | null | undefined,
  senderEmail: string | undefined,
): string {
  // A custom range is carried by the two dates, and `period` is null then.
  const window = startDate && endDate
    ? `${startDate} to ${endDate}`
    : PERIOD_LABELS[period ?? ''] ?? PERIOD_LABELS['30d']
  return `${window} · ${senderEmail ?? 'All mailboxes'}`
}

/**
 * Whether `data.campaign` still holds the campaign's LIFETIME counters.
 *
 * It does only in the combined view. Pick one inbox and the backend recomputes
 * that block from the filtered logs inside the selected window instead, because
 * leaving campaign-wide totals above a scoped chart would put two questions on
 * one screen and answer neither (build_campaign_detail). So the header is then
 * the same basis as the cards, and labelling it "all time" would be a plain
 * untruth as well as a duplicate.
 */
export const headerIsLifetime = (senderEmail: string | undefined): boolean => !senderEmail

/**
 * The health pill's wording and chrome (A3).
 *
 * `unknown` is its own state rather than a hidden pill: an inbox that was
 * picked for the campaign and has sent nothing is worth seeing, and painting it
 * green would claim a verdict the backend explicitly refused to give.
 */
export const HEALTH_META: Record<InboxHealth, { label: string; chip: string }> = {
  good: {
    label: 'Good',
    chip: 'bg-emerald-500/10 text-emerald-700 border-emerald-500/25 dark:bg-emerald-500/[0.12] dark:text-emerald-400 dark:border-emerald-500/25',
  },
  watch: {
    label: 'Watch',
    chip: 'bg-amber-500/10 text-amber-700 border-amber-500/25 dark:bg-amber-500/[0.12] dark:text-amber-400 dark:border-amber-500/25',
  },
  at_risk: {
    label: 'At-risk',
    chip: 'bg-red-500/10 text-red-700 border-red-500/25 dark:bg-red-500/[0.12] dark:text-red-400 dark:border-red-500/25',
  },
  unknown: {
    label: 'No data',
    chip: 'bg-muted/60 text-muted-foreground border-border/50 dark:bg-white/[0.05] dark:text-[#8a8f98] dark:border-white/[0.08]',
  },
}

/** The columns, in order. `numeric` right-aligns and applies tabular figures,
 *  so a column of rates reads as a column rather than ragged text. */
export const INBOX_COLUMNS = [
  { key: 'inbox',      label: 'Inbox',      numeric: false },
  { key: 'sent',       label: 'Sent',       numeric: true  },
  { key: 'open_rate',  label: 'Open rate',  numeric: true  },
  { key: 'click_rate', label: 'Click rate', numeric: true  },
  { key: 'reply_rate', label: 'Reply rate', numeric: true  },
  { key: 'health',     label: 'Health',     numeric: false },
] as const

/** Why an inbox got the verdict it did. The bounce rate drives it and is not a
 *  column of its own, so it is named here rather than left unexplained. */
export function healthReason(row: { health: InboxHealth; bounce_rate: number; sent: number }): string {
  if (row.health === 'unknown') return 'Nothing sent from this inbox in this period'
  return `Bounce rate ${row.bounce_rate.toFixed(1)}% over ${row.sent.toLocaleString()} sent`
}

/**
 * Which body the drawer shows. Data already in hand wins: a background refetch
 * that fails keeps the figures on screen rather than replacing them. Without
 * the error branch a failed request (404, 500, a timeout) left the skeleton up
 * forever, indistinguishable from a slow load.
 */
export function drawerView(isLoading: boolean, isError: boolean, hasData: boolean): 'loading' | 'error' | 'ready' {
  if (hasData) return 'ready'
  if (isError && !isLoading) return 'error'
  return 'loading'
}

/** The drawer when its request failed: why, a way to try again, a way out. */
function DrawerError({ titleId, message, onRetry, onClose }: {
  titleId: string
  message: string
  onRetry: () => void
  onClose: () => void
}) {
  return (
    <div className="p-6 space-y-5">
      <div className="flex items-start justify-between gap-3">
        <h2
          id={titleId}
          className="text-foreground dark:text-[#f7f8f8]"
          style={{ fontSize: '17px', fontWeight: 590, letterSpacing: '-0.182px', lineHeight: 1.3 }}
        >
          Campaign details
        </h2>
        <button
          type="button"
          onClick={onClose}
          aria-label="Close campaign details"
          className="p-1.5 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted/40 dark:text-[#8a8f98] dark:hover:text-[#f7f8f8] dark:hover:bg-white/[0.05] transition-colors duration-150 flex-shrink-0"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
      <div role="alert" className="flex flex-col items-center justify-center py-12 text-center text-muted-foreground dark:text-[#8a8f98]">
        <div className="h-12 w-12 rounded-md bg-red-500/10 dark:bg-red-500/[0.12] flex items-center justify-center mb-3">
          <WarningCircle className="h-5 w-5 text-red-600 dark:text-red-400" weight="fill" />
        </div>
        <p className="text-foreground dark:text-[#f7f8f8]" style={{ fontSize: '13px', fontWeight: 510, letterSpacing: '-0.13px' }}>
          Couldn't load this campaign's metrics
        </p>
        <p className="mt-1 max-w-sm" style={{ fontSize: '12px', letterSpacing: '-0.1px' }}>{message}</p>
        <button
          type="button"
          onClick={onRetry}
          className="mt-4 inline-flex items-center gap-1.5 rounded-md border border-border/60 dark:border-white/[0.08] px-3 py-1.5 text-foreground dark:text-[#f7f8f8] hover:bg-muted/40 dark:hover:bg-white/[0.05] transition-colors duration-150"
          style={{ fontSize: '12px', fontWeight: 510 }}
        >
          <ArrowClockwise className="h-3.5 w-3.5" />
          Try again
        </button>
      </div>
    </div>
  )
}

/**
 * The drawer while its data is in flight.
 *
 * It mirrors the real layout rather than centring a spinner, for two reasons:
 * nothing moves when the data lands, and the reader can see what is coming
 * (six figures, a table, a chart) instead of an unlabelled wait. The old
 * centred loader also left the drawer with no controls at all, so the only ways
 * out were Escape or the backdrop; the close button is real here.
 *
 * The card count is read off KPI_CARDS, so a seventh card can never arrive with
 * six placeholders in front of it.
 */
function DrawerSkeleton({ titleId, onClose }: { titleId: string; onClose: () => void }) {
  return (
    <div className="p-6 space-y-5" aria-busy="true">
      {/* The aria-labelledby target must exist in this branch too, or the
          drawer announces as an unnamed dialog while it loads. */}
      <h2 id={titleId} className="sr-only">Campaign details</h2>
      {/* Placeholders are decorative; this is what actually reaches a screen
          reader, which would otherwise be told nothing at all. */}
      <span role="status" className="sr-only">Loading campaign details</span>

      {/* Header: name, status chip, lifetime totals */}
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1 space-y-2">
          <Skeleton variant="text" width="55%" height="18px" />
          <div className="flex items-center gap-2">
            <Skeleton variant="rectangular" width="84px" height="18px" className="rounded-full" />
            <Skeleton variant="text" width="180px" height="12px" />
          </div>
        </div>
        <button
          type="button"
          onClick={onClose}
          aria-label="Close campaign details"
          className="p-1.5 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted/40 dark:text-[#8a8f98] dark:hover:text-[#f7f8f8] dark:hover:bg-white/[0.05] transition-colors duration-150 flex-shrink-0"
        >
          <X className="h-4 w-4" />
        </button>
      </div>

      {/* Inbox filter row, then the line naming the window and inbox */}
      <Skeleton variant="rectangular" width="260px" height="36px" />
      <Skeleton variant="text" width="220px" height="12px" />

      {/* One placeholder per metric card, built to KpiCard's own proportions:
          a small label, a 28px value and the reserved sparkline strip. */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {KPI_CARDS.map((card) => (
          <div
            key={card.key}
            className={cn(
              'flex flex-col gap-2.5 rounded-xl border p-5',
              'bg-card border-border/50',
              'dark:bg-white/[0.02] dark:border-white/[0.08]',
            )}
          >
            <Skeleton variant="text" width="55%" height="10px" />
            <div className="flex items-end justify-between gap-2">
              <Skeleton variant="text" width="45%" height="28px" />
              <Skeleton variant="rectangular" width="56px" height="18px" className="rounded-full" />
            </div>
            <div className="h-10 mt-1 flex items-end">
              <Skeleton variant="rectangular" width="100%" height="24px" />
            </div>
          </div>
        ))}
      </div>

      {/* Inbox statistics table */}
      <div className="space-y-3">
        <Skeleton variant="text" width="130px" height="14px" />
        <div
          className={cn(
            'rounded-lg border p-3 space-y-3',
            'bg-muted/20 border-border/50',
            'dark:bg-white/[0.02] dark:border-white/[0.06]',
          )}
        >
          {Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className="flex items-center gap-3">
              <Skeleton variant="text" className="flex-1" height="12px" />
              <Skeleton variant="text" width="48px" height="12px" />
              <Skeleton variant="text" width="48px" height="12px" />
              <Skeleton variant="rectangular" width="64px" height="18px" className="rounded-full" />
            </div>
          ))}
        </div>
      </div>

      {/* Daily timeline */}
      <div className="space-y-3">
        <Skeleton variant="text" width="110px" height="14px" />
        <div
          className={cn(
            'rounded-lg border p-3',
            'bg-muted/20 border-border/50',
            'dark:bg-white/[0.02] dark:border-white/[0.06]',
          )}
        >
          <Skeleton variant="rectangular" width="100%" height="250px" />
        </div>
      </div>

      {/* The two outcome blocks (A4), so the drawer does not grow by half a
          screen the moment the data lands. */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {['sentiment', 'pipeline'].map((block) => (
          <div key={block} className="space-y-3">
            <Skeleton variant="text" width="120px" height="14px" />
            <div
              className={cn(
                'rounded-lg border p-3',
                'bg-muted/20 border-border/50',
                'dark:bg-white/[0.02] dark:border-white/[0.06]',
              )}
            >
              <Skeleton variant="rectangular" width="100%" height="200px" />
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

export default function CampaignDrillDown({
  campaignId,
  onClose,
  userId,
  period,
  startDate,
  endDate,
  senderEmail,
  mailboxOptions,
  mailboxValue,
  onMailboxChange,
}: CampaignDrillDownProps) {
  const open = campaignId !== null
  useScrollLock(open)
  const chartTheme = useChartTheme()
  // Recharts animates via react-smooth (JS-driven), so it ignores both the global
  // CSS reduced-motion guard and framer-motion's <MotionConfig>. Gate it explicitly.
  const prefersReduced = useReducedMotion()
  const panelRef = useRef<HTMLDivElement>(null)
  /** Whatever had focus before we opened, so we can hand it back on close. */
  const restoreRef = useRef<HTMLElement | null>(null)
  // Unique per mounted instance so the aria-labelledby target can never collide
  // with a second drawer mid AnimatePresence exit.
  const uid = useId()
  const titleId = `${uid}-title`
  const mailboxId = `${uid}-mailbox`
  const inboxTableId = `${uid}-inboxes`

  const { data, isLoading, isError, error, refetch } = useQuery<CampaignDetailData>({
    // Period and dates belong in the key as well as the request: change the
    // window with the drawer open and it has to refetch, not keep showing the
    // previous window's cards.
    queryKey: ['analytics', 'campaign-detail', campaignId, userId, period, startDate, endDate, senderEmail],
    queryFn: () =>
      analyticsApi
        .getCampaignDetail(campaignId!, {
          period: period || undefined,
          start: startDate || undefined,
          end: endDate || undefined,
          user_id: userId ?? undefined,
          sender_email: senderEmail,
        })
        .then((r) => r.data),
    enabled: campaignId !== null,
    // One retry, not the default three: a 404 or a 500 will not fix itself,
    // and three back-to-back retries kept the skeleton up for several seconds
    // before the error could show.
    retry: 1,
    staleTime: 5 * 1000,
    refetchOnWindowFocus: true,
  })

  // A4's two outcome charts, derived up here rather than inside the JSX so the
  // hooks above and the markup below read in one order. `data` is undefined
  // until the first fetch settles, which is why both fall back to empty.
  const sentimentData = sentimentDonutData(data?.reply_sentiment ?? {})
  const sentimentSlices = sentimentData.filter((d) => d.value > 0)
  const sentimentTotal = sentimentData.reduce((sum, d) => sum + d.value, 0)
  const pipelineStages = data?.pipeline?.stages ?? []
  const reached = pipelineReached(pipelineStages)

  // Escape dismissal, bound on `document` like the other drawers
  // (MaterialPreviewDrawer, CrmImportModal). §57's prohibition is on binding
  // *activation* keys globally — Escape has no competing default here.
  useEffect(() => {
    if (!open) return
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', onKeyDown)
    return () => document.removeEventListener('keydown', onKeyDown)
  }, [open, onClose])

  // Focus in on open, back out on close (§16). Keyed on `open` alone so a
  // re-render for `isLoading` can never re-capture a node that lives *inside*
  // the drawer and is about to be destroyed.
  useEffect(() => {
    if (!open) return
    restoreRef.current = (document.activeElement as HTMLElement | null) ?? null
    // The body is still a loader on this frame, so there is nothing inside to
    // focus yet: focus the panel itself. `role="dialog"` + `aria-labelledby`
    // means that announces the drawer and its title, and Tab from here enters
    // the trap at the first control.
    panelRef.current?.focus()
    return () => {
      const previous = restoreRef.current
      restoreRef.current = null
      // The trigger may have unmounted while the drawer was open (a refetch can
      // drop the row) — `isConnected` guards that.
      if (previous?.isConnected && typeof previous.focus === 'function') {
        previous.focus()
      }
    }
  }, [open])

  /** Tab never leaves the drawer (§16). Scoped to the panel node, not `window`. */
  const trapTab = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key !== 'Tab') return
    const node = panelRef.current
    if (!node) return
    const focusables = Array.from(node.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR))
    // Swallowed even with nothing to land on, so the trap still holds while the
    // body is a loader.
    e.preventDefault()
    if (focusables.length === 0) return
    const current = focusables.indexOf(document.activeElement as HTMLElement)
    const next =
      current === -1
        // Focus is on the panel itself — restart at the end Tab's direction
        // implies rather than letting -1 arithmetic arrive there by accident.
        ? (e.shiftKey ? focusables[focusables.length - 1] : focusables[0])
        : (e.shiftKey
            ? focusables[(current === 0 ? focusables.length : current) - 1]
            : focusables[(current + 1) % focusables.length])
    next?.focus()
  }

  if (typeof document === 'undefined') return null

  const status = data ? campaignStatusMeta(data.campaign.status) : null
  const view = drawerView(isLoading, isError, Boolean(data && status))

  return createPortal(
    <AnimatePresence>
      {open && (
        // Modal band per §59 — a drawer portalled from a page is a modal, so it
        // sits at z-[100] with its own chrome one band down inside it. The old
        // z-[60] / z-[70] pair was off the published scale entirely.
        <div className="fixed inset-0 z-[100] backdrop-blur-md">
          {/* Backdrop — blur-only, no dark overlay (§45); `w-screen h-screen`
              rather than `inset-0` so it covers the scrollbar gutter (§29). */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed top-0 left-0 w-screen h-screen"
            onClick={onClose}
            aria-hidden="true"
          />

          <motion.div
            ref={panelRef}
            role="dialog"
            aria-modal="true"
            aria-labelledby={titleId}
            // Focusable-but-not-tabbable: clicking dead space inside the drawer
            // lands here instead of on <body>, so the Tab trap keeps working.
            tabIndex={-1}
            onKeyDown={trapTab}
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={{ type: 'spring', damping: 32, stiffness: 320 }}
            className={cn(
              // Wider than the 520px it was: six Overview-sized metric cards
              // need three columns to read as a row rather than a list, and the
              // timeline chart was cramped at the old width. Still capped at
              // the viewport, and still full-width on a phone.
              'fixed inset-y-0 right-0 z-10 flex flex-col w-full sm:w-[680px] lg:w-[860px] max-w-full overflow-y-auto outline-none',
              'bg-card border-l border-border/60',
              'dark:bg-[#0f1011] dark:border-white/[0.08]',
              'dark:shadow-[-24px_0_64px_rgba(0,0,0,0.55),inset_1px_0_0_rgba(255,255,255,0.02)]',
            )}
          >
            {view === 'error' ? (
              <DrawerError
                titleId={titleId}
                message={getErrorMessage(error, 'Something went wrong. Try again in a moment.')}
                onRetry={() => { void refetch() }}
                onClose={onClose}
              />
            ) : view === 'loading' || !data || !status ? (
              <DrawerSkeleton titleId={titleId} onClose={onClose} />
            ) : (
              <div className="p-6 space-y-5">
                {/* Header */}
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <h2
                      id={titleId}
                      className="text-foreground dark:text-[#f7f8f8] truncate"
                      style={{ fontSize: '17px', fontWeight: 590, letterSpacing: '-0.182px', lineHeight: 1.3 }}
                    >
                      {data.campaign.name}
                    </h2>
                    {/* Label, tone and glyph all come from the §60 state map —
                        `capitalize` on the raw enum printed "Partially_sent". */}
                    <span
                      className={cn(
                        'inline-flex items-center gap-1.5 mt-1.5 px-2 py-0.5 rounded-full border',
                        status.borderChipClass,
                      )}
                      style={{ fontSize: '10px', fontWeight: 590, letterSpacing: '0.04em' }}
                    >
                      <status.Icon className={cn('h-3 w-3', status.iconNudge)} weight="regular" aria-hidden="true" />
                      {status.label}
                    </span>
                    {/* The campaign's stored lifetime counters, which is what
                        the row in the list behind the drawer shows. Labelled so
                        it cannot be read as one of the period-scoped figures
                        below. Shown only in the combined view — see
                        headerIsLifetime for why one inbox changes the basis. */}
                    {headerIsLifetime(senderEmail) && (
                      <span
                        className="ml-2 text-muted-foreground dark:text-[#8a8f98] tabular-nums"
                        style={{ fontSize: '11px', letterSpacing: '-0.05px' }}
                      >
                        All time: {count(data.campaign.sent)} sent, {count(data.campaign.delivered)} delivered
                      </span>
                    )}
                  </div>
                  <button
                    type="button"
                    onClick={onClose}
                    aria-label="Close campaign details"
                    className="p-1.5 rounded-md text-muted-foreground hover:text-foreground hover:bg-muted/40 dark:text-[#8a8f98] dark:hover:text-[#f7f8f8] dark:hover:bg-white/[0.05] transition-colors duration-150 flex-shrink-0"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>

                {/* The A1 inbox filter, at the top of the view it scopes. The
                    drawer sits over the page header, so this is the only place
                    a reader can re-scope without closing it. Hidden when there
                    is nothing to choose between. */}
                {onMailboxChange && (mailboxOptions?.length ?? 0) > 1 && (
                  <div className="flex items-center gap-2">
                    {/* A real <label>, not an aria-label on the trigger: the
                        word is on screen anyway, so §56 wants the two to be the
                        same string rather than two competing names. */}
                    <label
                      htmlFor={mailboxId}
                      className="text-muted-foreground dark:text-[#8a8f98] uppercase flex-shrink-0"
                      style={{ fontSize: '10px', fontWeight: 590, letterSpacing: '0.06em' }}
                    >
                      Inbox
                    </label>
                    <Select
                      id={mailboxId}
                      value={mailboxValue ?? ''}
                      onChange={onMailboxChange}
                      icon={<Envelope className="h-4 w-4" weight="regular" />}
                      options={mailboxOptions ?? []}
                      className="w-full min-w-0 sm:w-[260px]"
                    />
                  </div>
                )}

                {/* What the cards below cover. The numbers in the line above
                    and the numbers in the cards are different questions, and
                    this is what tells them apart. */}
                <div
                  className="text-muted-foreground dark:text-[#8a8f98]"
                  style={{ fontSize: '11px', letterSpacing: '-0.05px' }}
                >
                  {describeScope(period, startDate, endDate, senderEmail)}
                </div>

                {/* The same six cards the Overview tab shows, measured over the
                    selected period and this inbox alone. The five flat tiles
                    that were here read off the campaign's lifetime counters, so
                    they carried no period, no comparison and no bounce or
                    engagement figure. KpiCard is reused rather than restyled so
                    the two views cannot drift apart. */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                  {KPI_CARDS.map((card) => (
                    <KpiCard
                      key={card.key}
                      label={card.label}
                      metric={metricOr0(data.kpis, card.key)}
                      sparkline={data.kpi_sparklines?.[card.key]}
                      format={card.format}
                      invertColor={card.invert}
                      color={BRAND_SERIES_COLOR}
                    />
                  ))}
                </div>

                {/* Inbox statistics (A3) — performance per sending inbox, so a
                    degrading mailbox is visible without leaving the campaign.
                    Above the timeline because this is the question the view is
                    for; the chart is context for it. */}
                <div>
                  <h3
                    id={inboxTableId}
                    className="text-foreground dark:text-[#f7f8f8] mb-3"
                    style={{ fontSize: '13px', fontWeight: 590, letterSpacing: '-0.13px' }}
                  >
                    Inbox statistics
                  </h3>
                  {data.inboxes?.length ? (
                    // Its own horizontal scroller: six columns do not fit a
                    // phone, and the drawer body must never scroll sideways.
                    <div
                      className={cn(
                        'rounded-lg border overflow-x-auto',
                        'bg-muted/20 border-border/50',
                        'dark:bg-white/[0.02] dark:border-white/[0.06]',
                      )}
                    >
                      <table className="w-full border-collapse" aria-labelledby={inboxTableId}>
                        <thead>
                          <tr className="border-b border-border/50 dark:border-white/[0.06]">
                            {INBOX_COLUMNS.map((col) => (
                              <th
                                key={col.key}
                                scope="col"
                                className={cn(
                                  'px-3 py-2 text-muted-foreground dark:text-[#8a8f98] uppercase whitespace-nowrap',
                                  col.numeric ? 'text-right' : 'text-left',
                                )}
                                style={{ fontSize: '10px', fontWeight: 590, letterSpacing: '0.06em' }}
                              >
                                {col.label}
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {data.inboxes.map((row) => (
                            <tr
                              key={row.inbox}
                              className="border-b border-border/40 last:border-0 dark:border-white/[0.04]"
                            >
                              {/* The address is the row's name, so it is a
                                  header cell rather than plain data (§56). */}
                              <th
                                scope="row"
                                className="px-3 py-2.5 text-left font-normal text-foreground dark:text-[#f7f8f8] max-w-[220px] truncate"
                                style={{ fontSize: '12px', letterSpacing: '-0.08px' }}
                                title={row.inbox}
                              >
                                {row.inbox}
                              </th>
                              {([
                                count(row.sent),
                                pct(row.open_rate),
                                pct(row.click_rate),
                                pct(row.reply_rate),
                              ]).map((value, i) => (
                                <td
                                  key={INBOX_COLUMNS[i + 1].key}
                                  className="px-3 py-2.5 text-right tabular-nums text-foreground dark:text-[#f7f8f8]"
                                  style={{ fontSize: '12px', letterSpacing: '-0.08px' }}
                                >
                                  {value}
                                </td>
                              ))}
                              <td className="px-3 py-2.5">
                                <span
                                  className={cn(
                                    'inline-flex items-center px-2 py-0.5 rounded-full border whitespace-nowrap',
                                    HEALTH_META[row.health].chip,
                                  )}
                                  style={{ fontSize: '10px', fontWeight: 590, letterSpacing: '0.04em' }}
                                  // The bounce rate drives the verdict and has
                                  // no column, so the pill carries the reason.
                                  title={healthReason(row)}
                                >
                                  {HEALTH_META[row.health].label}
                                </span>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <div className="flex flex-col items-center justify-center py-10 text-muted-foreground dark:text-[#8a8f98]">
                      <div className="h-12 w-12 rounded-md bg-muted/70 dark:bg-white/[0.04] dark:border dark:border-white/[0.05] flex items-center justify-center mb-3">
                        <Envelope className="h-5 w-5" weight="regular" />
                      </div>
                      <p style={{ fontSize: '13px', letterSpacing: '-0.13px' }}>
                        No inbox activity in this period.
                      </p>
                    </div>
                  )}
                </div>

                {/* Timeline Chart */}
                <div>
                  <h3
                    className="text-foreground dark:text-[#f7f8f8]"
                    style={{ fontSize: '13px', fontWeight: 590, letterSpacing: '-0.13px' }}
                  >
                    {timelineHeading(data.timeline)}
                  </h3>
                  {/* What the bars mean, in words, before the reader has to
                      work it out from the legend. */}
                  <p
                    className="text-muted-foreground dark:text-[#8a8f98] mt-0.5 mb-3"
                    style={{ fontSize: '12px', letterSpacing: '-0.1px' }}
                  >
                    Emails sent in each {isWeeklyBucket(data.timeline[0]?.date ?? '') ? 'week' : 'day'} of the period, and how many of them were opened, clicked or replied to.
                  </p>
                  {data.timeline.length > 0 ? (
                    <div
                      className={cn(
                        'rounded-lg border p-3',
                        'bg-muted/20 border-border/50',
                        'dark:bg-white/[0.02] dark:border-white/[0.06]',
                      )}
                    >
                      {/* `role="group"` so the name is actually exposed — an
                          `aria-label` on a bare div (role=generic) is dropped by
                          AT, and `role="img"` would hide the series that
                          recharts' accessibilityLayer just made explorable. */}
                      <div role="group" aria-label={`Sent, opened, clicked and replied counts over time for ${data.campaign.name}`}>
                        <ResponsiveContainer width="100%" height={260}>
                          {timelineChartKind(data.timeline.length) === 'bar' ? (
                            <BarChart accessibilityLayer data={data.timeline} margin={{ top: 8, right: 16, left: 0, bottom: 0 }} barCategoryGap="25%" barGap={2}>
                              <CartesianGrid strokeDasharray="3 3" stroke={chartTheme.grid} vertical={false} />
                              <XAxis dataKey="date" tickFormatter={(v) => formatBucket(String(v))} tick={{ fontSize: 11, fill: chartTheme.tickFill }} stroke={chartTheme.axis} strokeWidth={1} tickLine={false} />
                              <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: chartTheme.tickFill }} stroke={chartTheme.axis} strokeWidth={1} tickLine={false} axisLine={false} label={{ value: 'Emails', angle: -90, position: 'insideLeft', fontSize: 11, fill: chartTheme.tickFill }} />
                              <Tooltip
                                cursor={{ fill: chartTheme.grid, opacity: 0.4 }}
                                contentStyle={tooltipContentStyle(chartTheme)}
                                labelStyle={{ color: chartTheme.tooltipText, fontWeight: 590, marginBottom: 4 }}
                                labelFormatter={(v) => formatBucket(String(v), true)}
                                formatter={(value, name) => [`${count(Number(value ?? 0))} email${Number(value) === 1 ? '' : 's'}`, String(name)]}
                              />
                              <Legend wrapperStyle={{ fontSize: '12px', paddingTop: 8 }} iconType="circle" iconSize={8} />
                              {TIMELINE_SERIES.map(({ key, label }) => (
                                <Bar
                                  key={key}
                                  dataKey={key}
                                  name={label}
                                  fill={seriesColor(key)}
                                  radius={[3, 3, 0, 0]}
                                  maxBarSize={36}
                                  isAnimationActive={!prefersReduced}
                                />
                              ))}
                            </BarChart>
                          ) : (
                            <LineChart accessibilityLayer data={data.timeline} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
                              <CartesianGrid strokeDasharray="3 3" stroke={chartTheme.grid} vertical={false} />
                              <XAxis dataKey="date" tickFormatter={(v) => formatBucket(String(v))} tick={{ fontSize: 11, fill: chartTheme.tickFill }} stroke={chartTheme.axis} strokeWidth={1} tickLine={false} />
                              <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: chartTheme.tickFill }} stroke={chartTheme.axis} strokeWidth={1} tickLine={false} axisLine={false} label={{ value: 'Emails', angle: -90, position: 'insideLeft', fontSize: 11, fill: chartTheme.tickFill }} />
                              <Tooltip
                                cursor={{ stroke: chartTheme.axis, strokeDasharray: '3 3' }}
                                contentStyle={tooltipContentStyle(chartTheme)}
                                labelStyle={{ color: chartTheme.tooltipText, fontWeight: 590, marginBottom: 4 }}
                                labelFormatter={(v) => formatBucket(String(v), true)}
                                formatter={(value, name) => [`${count(Number(value ?? 0))} email${Number(value) === 1 ? '' : 's'}`, String(name)]}
                              />
                              <Legend wrapperStyle={{ fontSize: '12px', paddingTop: 8 }} iconType="circle" iconSize={8} />
                              {TIMELINE_SERIES.map(({ key, label }) => (
                                <Line
                                  key={key}
                                  type="monotone"
                                  dataKey={key}
                                  name={label}
                                  stroke={seriesColor(key)}
                                  // `replied` violet against `opened` indigo is ΔE 7.4 —
                                  // the dash carries the encoding a second way.
                                  strokeDasharray={seriesDash(key)}
                                  strokeWidth={2}
                                  // Dots so a bucket is visible even when its
                                  // neighbours are zero.
                                  dot={{ r: 3, strokeWidth: 0, fill: seriesColor(key) }}
                                  isAnimationActive={!prefersReduced}
                                />
                              ))}
                            </LineChart>
                          )}
                        </ResponsiveContainer>
                      </div>
                      {!hasEngagement(data.timeline) && (
                        <p
                          className="text-muted-foreground dark:text-[#8a8f98] mt-2"
                          style={{ fontSize: '12px', letterSpacing: '-0.1px' }}
                        >
                          No opens, clicks or replies recorded in this period yet, so only the Sent bars have height.
                        </p>
                      )}
                    </div>
                  ) : (
                    <div className="flex flex-col items-center justify-center py-12 text-muted-foreground dark:text-[#8a8f98]">
                      <div className="h-12 w-12 rounded-md bg-muted/70 dark:bg-white/[0.04] dark:border dark:border-white/[0.05] flex items-center justify-center mb-3">
                        <ChartLine className="h-5 w-5" weight="regular" />
                      </div>
                      <p style={{ fontSize: '13px', letterSpacing: '-0.13px' }}>No timeline data available.</p>
                    </div>
                  )}
                </div>

                {/* Outcomes (A4) — what came of the campaign, after what it
                    did. Side by side because they answer one question from two
                    ends: the tone of who replied, and where everyone it
                    reached now stands. Both honour the inbox filter above. */}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  {/* Reply sentiment. The same doughnut the Overview tab draws,
                      down to the hue and the slice order, because they plot the
                      same thing at different scopes (lib/sentiment). */}
                  <div>
                    <h3
                      className="text-foreground dark:text-[#f7f8f8]"
                      style={{ fontSize: '13px', fontWeight: 590, letterSpacing: '-0.13px' }}
                    >
                      Reply sentiment
                    </h3>
                    <p
                      className="text-muted-foreground dark:text-[#8a8f98] mt-0.5 mb-3"
                      style={{ fontSize: '12px', letterSpacing: '-0.1px' }}
                    >
                      {sentimentTotal > 0
                        ? `How the ${count(sentimentTotal)} ${sentimentTotal === 1 ? 'reply' : 'replies'} to this campaign read.`
                        : 'How replies to this campaign read.'}
                    </p>
                    <div
                      className={cn(
                        'rounded-lg border p-3',
                        'bg-muted/20 border-border/50',
                        'dark:bg-white/[0.02] dark:border-white/[0.06]',
                      )}
                    >
                      {sentimentTotal > 0 ? (
                        <div className="flex flex-col items-center" role="group" aria-label="Reply sentiment for this campaign">
                          <ResponsiveContainer width="100%" height={200}>
                            <PieChart accessibilityLayer>
                              <Pie
                                data={sentimentSlices}
                                cx="50%"
                                cy="50%"
                                innerRadius={50}
                                outerRadius={80}
                                paddingAngle={2}
                                dataKey="value"
                                isAnimationActive={!prefersReduced}
                              >
                                {sentimentSlices.map((entry) => (
                                  <Cell key={entry.key} fill={entry.color} />
                                ))}
                              </Pie>
                              <Tooltip contentStyle={tooltipContentStyle(chartTheme)} />
                            </PieChart>
                          </ResponsiveContainer>
                          {/* Zero buckets stay in the legend and out of the arc:
                              recharts pads a zero sector into a visible sliver,
                              but an absent legend row reads as "not tracked"
                              rather than "none". Same split as the Overview. */}
                          <div className="flex flex-wrap gap-2.5 mt-3 justify-center">
                            {sentimentData.map((d) => (
                              <div
                                key={d.key}
                                className="inline-flex items-center gap-1.5 text-muted-foreground dark:text-[#8a8f98]"
                                style={{ fontSize: '11px', letterSpacing: '-0.05px' }}
                              >
                                <span
                                  className="h-2 w-2 rounded-full"
                                  style={
                                    d.value > 0
                                      ? { backgroundColor: d.color }
                                      : { boxShadow: `inset 0 0 0 1px ${d.color}`, opacity: 0.5 }
                                  }
                                />
                                <span className={d.value > 0 ? undefined : 'opacity-60'}>{d.name}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      ) : (
                        <div className="flex flex-col items-center justify-center py-10 text-muted-foreground dark:text-[#8a8f98]">
                          <div className="h-12 w-12 rounded-md bg-muted/70 dark:bg-white/[0.04] dark:border dark:border-white/[0.05] flex items-center justify-center mb-3">
                            <ChatCircle className="h-5 w-5" weight="regular" />
                          </div>
                          <p style={{ fontSize: '13px', letterSpacing: '-0.13px' }}>No replies in this period.</p>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Contact pipeline. Same bars and the same ordinal ramp as
                      the Contacts tab's funnel; only the population differs. */}
                  <div>
                    <h3
                      className="text-foreground dark:text-[#f7f8f8]"
                      style={{ fontSize: '13px', fontWeight: 590, letterSpacing: '-0.13px' }}
                    >
                      Contact pipeline
                    </h3>
                    {/* The denominator, in words. Without it the percentages
                        look like shares of the four stages, which they are not
                        -- a contact can be unqualified or have no status and
                        was still reached. */}
                    <p
                      className="text-muted-foreground dark:text-[#8a8f98] mt-0.5 mb-3"
                      style={{ fontSize: '12px', letterSpacing: '-0.1px' }}
                    >
                      Where the {count(reached)} {reached === 1 ? 'contact' : 'contacts'} this campaign emailed stand today.
                    </p>
                    <div
                      className={cn(
                        'rounded-lg border p-3',
                        'bg-muted/20 border-border/50',
                        'dark:bg-white/[0.02] dark:border-white/[0.06]',
                      )}
                    >
                      {!pipelineIsEmpty(pipelineStages) ? (
                        <div className="space-y-4">
                          <div className="space-y-2.5">
                            {pipelineStages.map((stage, i) => (
                              <div key={stage.name} className="flex items-center gap-3">
                                <span
                                  className="text-muted-foreground dark:text-[#8a8f98] w-20 flex-shrink-0 text-right capitalize truncate"
                                  style={{ fontSize: '11px', fontWeight: 510, letterSpacing: '-0.1px' }}
                                >
                                  {stage.name.replace(/_/g, ' ')}
                                </span>
                                <div
                                  className="relative flex-1 min-w-0 h-7 rounded-md overflow-hidden bg-muted/40 dark:bg-white/[0.04]"
                                  aria-hidden="true"
                                >
                                  <div
                                    className="h-full rounded-md transition-all duration-300"
                                    style={{
                                      width: `${funnelWidth(stage.pct)}%`,
                                      backgroundColor: sequentialStep(chartTheme, i, pipelineStages.length),
                                    }}
                                  />
                                </div>
                                {/* Value outside the track, as on the Contacts
                                    tab: on it, dark text over the ramp's last
                                    steps fell below the 4.5:1 floor. */}
                                <span className="flex items-baseline justify-end gap-1.5 w-[92px] flex-shrink-0 whitespace-nowrap">
                                  <span
                                    className="text-foreground dark:text-[#f7f8f8] tabular-nums"
                                    style={{ fontSize: '12px', fontWeight: 590, letterSpacing: '-0.1px' }}
                                  >
                                    {count(stage.count)}
                                  </span>
                                  <span
                                    className="text-muted-foreground dark:text-[#8a8f98] tabular-nums"
                                    style={{ fontSize: '11px', fontWeight: 510, letterSpacing: '-0.05px' }}
                                  >
                                    {stage.pct.toFixed(1)}%
                                  </span>
                                </span>
                              </div>
                            ))}
                          </div>
                          {data.pipeline.conversions.length > 0 && (
                            <div className="border-t border-border/50 dark:border-white/[0.06] pt-3 space-y-1">
                              <p
                                className="text-muted-foreground dark:text-[#8a8f98] uppercase mb-1.5"
                                style={{ fontSize: '10px', fontWeight: 590, letterSpacing: '0.06em' }}
                              >
                                Conversion rates
                              </p>
                              {data.pipeline.conversions.map((conversion) => (
                                <div
                                  key={`${conversion.from}-${conversion.to}`}
                                  className="flex items-center justify-between rounded-md px-2 py-1.5"
                                >
                                  <span
                                    className="text-muted-foreground dark:text-[#8a8f98] capitalize"
                                    style={{ fontSize: '12px', letterSpacing: '-0.1px' }}
                                  >
                                    {conversion.from} to {conversion.to}
                                  </span>
                                  <span
                                    className="text-foreground dark:text-[#f7f8f8] tabular-nums"
                                    style={{ fontSize: '12px', fontWeight: 590, letterSpacing: '-0.1px' }}
                                  >
                                    {pct(conversion.rate)}
                                  </span>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      ) : (
                        <div className="flex flex-col items-center justify-center py-10 text-muted-foreground dark:text-[#8a8f98]">
                          <div className="h-12 w-12 rounded-md bg-muted/70 dark:bg-white/[0.04] dark:border dark:border-white/[0.05] flex items-center justify-center mb-3">
                            <UsersThree className="h-5 w-5" weight="regular" />
                          </div>
                          <p style={{ fontSize: '13px', letterSpacing: '-0.13px' }}>No contacts reached in this period.</p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </motion.div>
        </div>
      )}
    </AnimatePresence>,
    document.body,
  )
}
